import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
  RouterOutlet
} from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { CommonModule } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';

import { TopbarComponent } from './pages/topbar/topbar.component';
import { SidebarComponent } from './pages/sidebar/sidebar.component';
import { SpinnerComponent } from './shared/spinner/spinner.component';
import { AuthService } from './services/auth/auth.service';
import { CommonService } from './services/common.service';
import { GlobalResizeService } from './services/global-resize/global-resize.service';
import { ThemeService } from './services/theme/theme.service';
import { FileUploadBoxComponent } from "./shared/file-upload-box/file-upload-box.component";
import { Constants } from './common/constants';
import { Subscription } from 'rxjs';
import { DeploymentVersionService } from './services/deployment-version/deployment-version.service';
import { RouteComponentPreloaderService } from './services/route-preload/route-component-preloader.service';

@Component({
    selector: 'app-root',
    imports: [
        RouterOutlet,
        CommonModule,
        TopbarComponent,
        SidebarComponent,
        MatSidenavModule,
        MatButtonModule,
        SpinnerComponent,
        MatProgressBarModule,
        FileUploadBoxComponent
    ],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {

  title = 'zignflix-agent';
  isSidebarCollapsed = true;
  private appIsLoading = false;
  private routeChunkLoadCount = 0;
  private routeChunkLoading = false;
  private deploymentUpdateLoading = false;
  private routeChunkOverlayTimer?: number;
  private loadingStateSubscription?: Subscription;
  private routeLoadingSubscription?: Subscription;
  private deploymentUpdateSubscription?: Subscription;

  constructor(private authService: AuthService, private commonService: CommonService, private globalResizeService: GlobalResizeService, private themeService: ThemeService, private deploymentVersionService: DeploymentVersionService, private routeComponentPreloaderService: RouteComponentPreloaderService, private router: Router) {
    window.addEventListener('beforeunload', this.beforeUnloadHandler.bind(this));
    this.isSidebarCollapsed = this.getSidebarState();
    this.appIsLoading = this.commonService.isLoading;
    this.themeService.initialize();
  }

  ngOnInit() {
    this.deploymentVersionService.start();
    if (this.isLoggedIn()) {
      this.routeComponentPreloaderService.start();
    }

    this.loadingStateSubscription = this.commonService.loadingState$.subscribe((isLoading) => {
      // Routed components can update the shared loading state while RouterOutlet is
      // being created. Apply the root @if state in the next microtask so the same
      // change-detection pass never observes two different block indexes.
      queueMicrotask(() => {
        this.appIsLoading = isLoading;
      });
    });

    this.commonService.currentUserProfile$.subscribe((user) => {
      if (user && user.email) {
        this.routeComponentPreloaderService.start();
        this.themeService.initialize(user.email);
        const savedStateStr = localStorage.getItem(Constants.SIDEBAR_STATE_KEY);
        if (savedStateStr) {
          try {
            const parsed = JSON.parse(savedStateStr);
            if (parsed && typeof parsed === 'object' && user.email in parsed) {
              this.isSidebarCollapsed = parsed[user.email];
              return;
            }
          } catch (e) {
            // Safe fallback
          }
        }
        this.isSidebarCollapsed = true;
      }
    });

    this.routeLoadingSubscription = this.router.events.subscribe((event) => {
      if (event instanceof RouteConfigLoadStart) {
        this.startRouteChunkOverlay();
        return;
      }

      if (event instanceof RouteConfigLoadEnd) {
        this.stopRouteChunkOverlay();
        return;
      }

      if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        this.clearRouteChunkOverlay();
      }
    });

    this.deploymentUpdateSubscription = this.deploymentVersionService.updateInProgress$.subscribe((isUpdating) => {
      queueMicrotask(() => {
        this.deploymentUpdateLoading = isUpdating;
      });
    });
  }

  isLoggedIn() {
    return this.authService.isLoggedIn();
  }

  isLoading() {
    return this.appIsLoading;
  }

  isVersionUpdateOverlayVisible() {
    return this.routeChunkLoading || this.deploymentUpdateLoading;
  }

  ngOnDestroy(): void {
    this.loadingStateSubscription?.unsubscribe();
    this.routeLoadingSubscription?.unsubscribe();
    this.deploymentUpdateSubscription?.unsubscribe();
    if (this.routeChunkOverlayTimer) {
      window.clearTimeout(this.routeChunkOverlayTimer);
    }
    this.deploymentVersionService.ngOnDestroy();
    this.routeComponentPreloaderService.ngOnDestroy();
  }

  isFileUploadVisible() {
    return this.commonService.filesToUpload?.length > 0;
  }

  beforeUnloadHandler(event: BeforeUnloadEvent) {
    if (this.isFileUploadVisible()) {
      event.preventDefault();
    }
  }


  toggleSidebar() {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
    const user = this.commonService.currentUserProfile$.value;

    let savedStateObj: Record<string, boolean> = {};
    const savedStateStr = localStorage.getItem(Constants.SIDEBAR_STATE_KEY);
    if (savedStateStr) {
      try {
        const parsed = JSON.parse(savedStateStr);
        if (parsed && typeof parsed === 'object') {
          savedStateObj = parsed;
        }
      } catch (e) {
        // Safe to overwrite
      }
    }

    if (user && user.email) {
      savedStateObj[user.email] = this.isSidebarCollapsed;
      localStorage.setItem(Constants.SIDEBAR_STATE_KEY, JSON.stringify(savedStateObj));
    } else {
      localStorage.setItem(Constants.SIDEBAR_STATE_KEY, JSON.stringify(this.isSidebarCollapsed));
    }
  }

  private startRouteChunkOverlay(): void {
    this.routeChunkLoadCount += 1;
    if (this.routeChunkLoading || this.routeChunkOverlayTimer) return;

    this.routeChunkOverlayTimer = window.setTimeout(() => {
      this.routeChunkOverlayTimer = undefined;
      if (this.routeChunkLoadCount > 0) {
        this.routeChunkLoading = true;
      }
    }, 180);
  }

  private stopRouteChunkOverlay(): void {
    this.routeChunkLoadCount = Math.max(0, this.routeChunkLoadCount - 1);
    if (this.routeChunkLoadCount === 0) {
      this.clearRouteChunkOverlay();
    }
  }

  private clearRouteChunkOverlay(): void {
    this.routeChunkLoadCount = 0;
    if (this.routeChunkOverlayTimer) {
      window.clearTimeout(this.routeChunkOverlayTimer);
      this.routeChunkOverlayTimer = undefined;
    }
    this.routeChunkLoading = false;
  }

  getSidebarState(): boolean {
    if (!this.commonService || !this.commonService.currentUserProfile$) {
      return true;
    }
    const user = this.commonService.currentUserProfile$.value;
    const savedStateStr = localStorage.getItem(Constants.SIDEBAR_STATE_KEY);
    if (!savedStateStr) {
      return true;
    }

    try {
      const parsed = JSON.parse(savedStateStr);
      if (user && user.email) {
        if (parsed && typeof parsed === 'object' && user.email in parsed) {
          return parsed[user.email];
        }
      }
      if (typeof parsed === 'boolean') {
        return parsed;
      }
    } catch (e) {
      if (savedStateStr === 'true') return true;
      if (savedStateStr === 'false') return false;
    }

    return true;
  }


}
